/**
 * Demo app here: https://github.com/coinbase/onramp-demo-app/
 * Docs:
 *  - https://www.coinbase.com/developer-platform/discover/launches/zero-fee-usdc
 *  - https://docs.cdp.coinbase.com/onramp/docs/api-offramp-overview
 *  - https://docs.cdp.coinbase.com/onramp/docs/quickstart
 *  - CDP Portal: https://portal.cdp.coinbase.com/projects/
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import debounce from 'lodash/debounce'
import { toast } from 'react-toastify'

import { ChainId, getChainNameFromChainId } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import BuySection from '@src/pages/FiatExchange/components/BuySection'
import ExchangeButton from '@src/pages/FiatExchange/components/ExchangeButton'
import PaymentMethodSelectionContainer from '@src/pages/FiatExchange/components/PaymentMethodSelectionContainer'
import SellSection from '@src/pages/FiatExchange/components/SellSection'
import { RampDirection, ToggleRampDirection } from '@src/pages/FiatExchange/components/ToggleRampDirection'
import {
    COINBASE_PROJECT_ID,
    DEFAULT_ONRAMP_FIAT_CURRENCY,
    DEFAULT_ONRAMP_TOKEN,
    supportedTokens,
} from '@src/pages/FiatExchange/config'
import { useCoinBaseOfframpQuote } from '@src/pages/FiatExchange/hooks/useCoinBaseOfframpQuote'
import { useCoinBaseOnrampQuote } from '@src/pages/FiatExchange/hooks/useCoinBaseOnrampQuote'
import { useCoinbaseSecureToken } from '@src/pages/FiatExchange/hooks/useCoinbaseSecureToken'
import { getOfframpSellURL, getOnrampBuyURL } from '@src/pages/FiatExchange/utils/coinbaseUrlBuilder'
import { BuyQuoteRequest, PaymentMethod, SellQuoteRequest, SessionTokenRequest } from '@src/types/coinbase'
import { FiatCurrency } from '@src/types/fiat'
import { IToken } from '@src/types/token'
import { createContextLogger } from '@src/utils'

const logger = createContextLogger('FiatExchange')

interface FiatExchangeProps {
    evmWalletAddress?: string
}

const FiatExchange = (props: FiatExchangeProps) => {
    const { evmWalletAddress, isEVMConnected } = useWalletConnections()

    // State for tracking the ramp direction (onramp or offramp)
    const [rampDirection, setRampDirection] = useState<RampDirection>('ONRAMP')

    // New states for managing sell and buy values
    const [sellValue, setSellValue] = useState('0')
    const [buyValue, setBuyValue] = useState(0)

    const handleSellValueChange = (newValue: string) => {
        if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
            // Trim leading zeros if necessary
            let trimmedValue = newValue
            if (newValue !== '') {
                const parts = newValue.split('.')
                if (parts[0].length > 1) {
                    // Trim leading zeros in the integer part
                    const trimmedInt = parts[0].replace(/^0+/, '')
                    // Ensure at least one zero remains if all digits were zeros
                    parts[0] = trimmedInt === '' ? '0' : trimmedInt
                    trimmedValue = parts.join('.')
                }
            }
            setSellValue(trimmedValue)
            logger.debug('Sell value updated to:', trimmedValue)
        } else {
            logger.warn('Invalid numeric input:', newValue)
        }
    }

    // State to store the generated URL and loading state
    const [generatedUrl, setGeneratedUrl] = useState<string | null>(null)
    const [isExchangeInProgress, setIsExchangeInProgress] = useState(false)
    const [isQuoteFetching, setIsQuoteFetching] = useState(false)

    // Initialize buy and sell entities based on ramp direction
    const [buyToken, setBuyToken] = useState<IToken>(DEFAULT_ONRAMP_TOKEN)
    const [sellToken, setSellToken] = useState<IToken>(DEFAULT_ONRAMP_TOKEN) // For offramp mode
    const [buyCurrency, setBuyCurrency] = useState<FiatCurrency | undefined>(DEFAULT_ONRAMP_FIAT_CURRENCY) // For offramp mode
    const [sellCurrency, setSellCurrency] = useState<FiatCurrency>(DEFAULT_ONRAMP_FIAT_CURRENCY)
    const [onrampPaymentMethod, setOnrampPaymentMethod] = useState<PaymentMethod | undefined>()
    const [offrampPaymentMethod, setOfframpPaymentMethod] = useState<PaymentMethod | undefined>()

    const onExchange = async () => {
        logger.info('onExchange triggered for', rampDirection)
        setIsExchangeInProgress(true)
        logger.info('Using session token from hook. Token ready:', isSessionTokenReady)

        try {
            if (!sessionToken) {
                throw new Error('Session token not available')
            }

            if (rampDirection === 'ONRAMP') {
                // Handle onramp exchange
                if (buyQuote) {
                    logger.info('Building onramp URL with session token and quote.')

                    // Use the memoized URL that includes the payment method
                    const url = memoizedBuyURL

                    if (url) {
                        logger.success('Onramp URL generated successfully:', url)
                        // Store the URL and open it
                        setGeneratedUrl(url)
                        window.open(url, '_blank', 'noopener,noreferrer')
                    } else {
                        throw new Error('Failed to generate onramp URL')
                    }
                } else {
                    throw new Error('Buy quote not available for onramp')
                }
            } else if (rampDirection === 'OFFRAMP') {
                // Handle offramp exchange
                if (sellQuote) {
                    logger.info('Building offramp URL with session token and quote.')

                    // Use the memoized URL that includes the payment method
                    const url = memoizedSellURL

                    if (url) {
                        logger.success('Offramp URL generated successfully:', url)
                        // Store the URL and open it
                        setGeneratedUrl(url)
                        window.open(url, '_blank', 'noopener,noreferrer')
                    } else {
                        throw new Error('Failed to generate offramp URL')
                    }
                } else {
                    throw new Error('Sell quote not available for offramp')
                }
            }
        } catch (error) {
            logger.error('Error during exchange:', error)
            toast.error(`Exchange failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
        } finally {
            setIsExchangeInProgress(false)
        }
    }

    // Extract unique token symbols from supportedTokens for assets field
    const supportedAssets = useMemo(() => {
        const uniqueSymbols = new Set<string>()
        supportedTokens.forEach(token => {
            uniqueSymbols.add(token.symbol)
        })
        return Array.from(uniqueSymbols)
    }, [])

    // Extract unique blockchain names from supportedTokens for blockchains field
    const supportedBlockchains = useMemo(() => {
        const uniqueChains = new Set<string>()
        supportedTokens.forEach(token => {
            try {
                // Check if this chainId exists in CHAIN_IDS by trying to find its key
                const chainName = getChainNameFromChainId(token.chainId)
                // Add to our set if it's a valid chain name
                // This cast is safe since we're checking for validity
                uniqueChains.add(chainName.toLowerCase())
            } catch (e) {
                // Skip invalid chains
                logger.warn('Skipping invalid chain ID:', token.chainId)
            }
        })
        return Array.from(uniqueChains)
    }, [])

    // Create the session token request with all required fields
    const sessionTokenRequest = useMemo(() => {
        if (!evmWalletAddress) return null

        return {
            addresses: [
                {
                    address: evmWalletAddress,
                    blockchains: supportedBlockchains,
                },
            ],
            assets: supportedAssets,
        } as SessionTokenRequest
    }, [evmWalletAddress, supportedBlockchains, supportedAssets])

    const { sessionToken, isSessionTokenReady, isSessionTokenFetching, sessionTokenError } = useCoinbaseSecureToken(
        sessionTokenRequest || {
            addresses: [],
            assets: [],
        }
    )

    // State for quote requests - separate for onramp and offramp
    const [onrampQuoteRequest, setOnrampQuoteRequest] = useState<BuyQuoteRequest | null>(null)
    const [offrampQuoteRequest, setOfframpQuoteRequest] = useState<SellQuoteRequest | null>(null)

    // Optimize onramp parameters with useMemo
    const onrampParameters = useMemo(
        () => ({
            network: buyToken ? getChainNameFromChainId(buyToken.chainId).toLowerCase() : 'base',
            buyAsset: buyToken?.symbol || 'USDC',
            fiatCurrency: sellCurrency?.symbol || 'USD',
        }),
        [buyToken, sellCurrency]
    )

    // Optimize offramp parameters with useMemo
    const offrampParameters = useMemo(
        () => ({
            network: sellToken ? getChainNameFromChainId(sellToken.chainId).toLowerCase() : 'base',
            sellAsset: sellToken?.symbol || 'USDC',
            cashoutCurrency: buyCurrency?.symbol || 'USD',
        }),
        [sellToken, buyCurrency]
    )

    // Debounced function to update the onramp quote request
    const debouncedUpdateOnrampQuote = useMemo(
        () =>
            debounce((value: string, currency: FiatCurrency, token: IToken) => {
                logger.debug('debouncedUpdateOnrampQuote executing with:', {
                    value,
                    currencySymbol: currency.symbol,
                })

                if (sessionToken && !isSessionTokenFetching && parseFloat(value) > 0) {
                    const newQuoteRequest: BuyQuoteRequest = {
                        purchase_currency: token.symbol,
                        payment_amount: value,
                        payment_currency: currency.symbol,
                        payment_method: onrampPaymentMethod?.id || 'CARD',
                        country: 'US',
                        purchase_network: getChainNameFromChainId(token.chainId).toLowerCase(),
                    }
                    logger.info('Creating new onramp quote request:', newQuoteRequest)
                    setOnrampQuoteRequest(newQuoteRequest)
                } else {
                    logger.debug('Not creating onramp quote request, conditions not met:', {
                        hasSessionToken: !!sessionToken,
                        isSessionTokenFetching,
                        valueAsNumber: parseFloat(value),
                        isValuePositive: parseFloat(value) > 0,
                    })
                    setOnrampQuoteRequest(null)
                }
            }, 500),
        [sessionToken, isSessionTokenFetching]
    )

    // Debounced function to update the offramp quote request
    const debouncedUpdateOfframpQuote = useMemo(
        () =>
            debounce((value: string, token: IToken, currency: FiatCurrency) => {
                logger.debug('debouncedUpdateOfframpQuote executing with:', {
                    value,
                    tokenSymbol: token.symbol,
                })

                if (sessionToken && !isSessionTokenFetching && parseFloat(value) > 0) {
                    const newQuoteRequest: SellQuoteRequest = {
                        sell_currency: token.symbol,
                        sell_amount: value,
                        cashout_currency: currency.symbol,
                        payment_method: offrampPaymentMethod?.id || 'ACH_BANK_ACCOUNT',
                        country: 'US',
                        sell_network: getChainNameFromChainId(token.chainId).toLowerCase(),
                    }
                    logger.info('Creating new offramp quote request:', newQuoteRequest)
                    setOfframpQuoteRequest(newQuoteRequest)
                } else {
                    logger.debug('Not creating offramp quote request, conditions not met:', {
                        hasSessionToken: !!sessionToken,
                        isSessionTokenFetching,
                        valueAsNumber: parseFloat(value),
                        isValuePositive: parseFloat(value) > 0,
                    })
                    setOfframpQuoteRequest(null)
                }
            }, 500),
        [sessionToken, isSessionTokenFetching]
    )

    useEffect(() => {
        if (rampDirection === 'ONRAMP' && sellValue && sellCurrency && buyToken) {
            debouncedUpdateOnrampQuote(sellValue, sellCurrency, buyToken)
        } else if (rampDirection === 'OFFRAMP' && sellValue && sellToken && buyCurrency) {
            debouncedUpdateOfframpQuote(sellValue, sellToken, buyCurrency)
        }
    }, [
        rampDirection,
        debouncedUpdateOnrampQuote,
        debouncedUpdateOfframpQuote,
        sellValue,
        sellCurrency,
        buyToken,
        sellToken,
        buyCurrency,
    ])

    const {
        data: buyQuote,
        isFetching: isOnrampQuoteFetching,
        error: onrampQuoteError,
    } = useCoinBaseOnrampQuote(onrampQuoteRequest)

    const {
        data: sellQuote,
        isFetching: isOfframpQuoteFetching,
        error: offrampQuoteError,
    } = useCoinBaseOfframpQuote(offrampQuoteRequest)

    // Check if sell value is zero or empty
    const isSellValueZeroOrEmpty = useMemo(() => {
        return !sellValue || sellValue === '0' || sellValue === '' || parseFloat(sellValue) === 0
    }, [sellValue])

    // Track when a quote is fully ready for exchange
    const isQuoteReady = useMemo(() => {
        if (rampDirection === 'ONRAMP') {
            // Onramp quote is ready when we have a valid buy quote that matches the current input amount
            return !!buyQuote && !isOnrampQuoteFetching && !isSellValueZeroOrEmpty && !!sessionToken
        } else {
            // Offramp quote is ready when we have a valid sell quote that matches the current input amount
            return !!sellQuote && !isOfframpQuoteFetching && !isSellValueZeroOrEmpty && !!sessionToken
        }
    }, [
        rampDirection,
        buyQuote,
        sellQuote,
        isOnrampQuoteFetching,
        isOfframpQuoteFetching,
        isSellValueZeroOrEmpty,
        sessionToken,
    ])

    // Update buy value when a new sell quote is received (offramp)
    useEffect(() => {
        if (sellQuote && rampDirection === 'OFFRAMP') {
            // Extract the total value from the quote
            const totalValue = parseFloat(sellQuote.cashout_total.value)
            logger.debug('Offramp quote received, updating buy value:', totalValue)
            setBuyValue(totalValue)
        }
    }, [sellQuote, rampDirection])

    // Update buy value when a new buy quote is received (onramp)
    useEffect(() => {
        if (buyQuote && rampDirection === 'ONRAMP') {
            // Extract the purchase amount from the quote
            const purchaseAmount = parseFloat(buyQuote.purchase_amount.value)
            logger.debug('Onramp quote received, updating buy value:', purchaseAmount)
            setBuyValue(purchaseAmount)
        }
    }, [buyQuote, rampDirection])

    // Memoize the most recent generated onramp URL
    const memoizedBuyURL = useMemo(() => {
        return getOnrampBuyURL({
            sessionToken,
            projectId: COINBASE_PROJECT_ID || '',
            walletAddress: evmWalletAddress,
            network: onrampParameters.network,
            buyAsset: onrampParameters.buyAsset,
            presetFiatAmount: sellValue,
            fiatCurrency: onrampParameters.fiatCurrency,
            buyQuote,
            // Pass payment method ID for defaultPaymentMethod URL parameter
            paymentMethod: onrampPaymentMethod?.id,
        })
    }, [sessionToken, buyQuote, onrampParameters, evmWalletAddress, sellValue, onrampPaymentMethod])

    // Memoize the most recent generated offramp URL
    const memoizedSellURL = useMemo(() => {
        return getOfframpSellURL({
            sessionToken,
            projectId: COINBASE_PROJECT_ID || '',
            walletAddress: evmWalletAddress,
            networks: [offrampParameters.network],
            sellQuote,
            // Pass payment method ID for defaultPaymentMethod URL parameter
            paymentMethod: offrampPaymentMethod?.id,
            // Pass payment method ID as defaultCashoutMethod as well
            defaultCashoutMethod: offrampPaymentMethod?.id,
        })
    }, [sessionToken, sellQuote, offrampParameters, evmWalletAddress, offrampPaymentMethod])

    const handleBuyTokenChange = (newToken: IToken) => {
        setBuyToken(newToken)
        logger.debug('Token details:', newToken)
    }

    const handleSellCurrencyChange = (newCurrency: FiatCurrency) => {
        setSellCurrency(newCurrency)
        logger.debug('Currency details:', newCurrency)
    }

    // Handler for buy currency change (for offramp)
    const handleBuyCurrencyChange = (newCurrency: FiatCurrency) => {
        setBuyCurrency(newCurrency)
        logger.debug('Buy currency details:', newCurrency)
    }

    // Get the required chain type based on the selected buy token
    const requiredChainType = useMemo(() => {
        if (!buyToken) return 'EVM'

        switch (buyToken.chainId) {
            case ChainId.Solana:
                return 'Solana'
            case ChainId.Tron:
                return 'Tron'
            default:
                return 'EVM'
        }
    }, [buyToken])

    return (
        <div className='w-full max-w-[520px] rounded-2xl border border-mach-modal-border bg-mach-modal-background px-4 py-4'>
            {/* Header */}
            <div className='mb-4 flex items-center justify-between'>
                <span className='text-xl font-medium leading-7'>Fiat Exchange</span>
            </div>

            {/* Main content */}
            <div className='flex flex-col gap-4'>
                {/* Exchange inputs */}
                <div className='space-y-[6px]'>
                    {rampDirection === 'ONRAMP' ? (
                        <SellSection
                            value={sellValue}
                            onInputValueChange={handleSellValueChange}
                            onCurrencyChanged={handleSellCurrencyChange}
                            selectedPaymentMethod={onrampPaymentMethod}
                        />
                    ) : (
                        <SellSection
                            value={sellValue}
                            onInputValueChange={handleSellValueChange}
                            onTokenChanged={setSellToken}
                            isOfframp={true}
                            selectedPaymentMethod={offrampPaymentMethod}
                        />
                    )}
                    <ToggleRampDirection
                        direction={rampDirection}
                        onToggleRampDirection={() => {
                            logger.info(`Toggling ramp direction from ${rampDirection}`)
                            setRampDirection(prev => (prev === 'ONRAMP' ? 'OFFRAMP' : 'ONRAMP'))
                            // Reset values when switching modes
                            setSellValue('0')
                            setBuyValue(0)
                        }}
                    />
                    {rampDirection === 'ONRAMP' ? (
                        <BuySection
                            value={buyValue}
                            onBuyTokenChange={handleBuyTokenChange}
                            isLoading={isQuoteFetching}
                        />
                    ) : (
                        <BuySection
                            value={buyValue}
                            onBuyCurrencyChange={handleBuyCurrencyChange}
                            isLoading={isQuoteFetching}
                            isOfframp={true}
                        />
                    )}
                </div>

                {/* Payment Method Selection */}
                <PaymentMethodSelectionContainer
                    rampDirection={rampDirection}
                    selectedPaymentMethod={rampDirection === 'ONRAMP' ? onrampPaymentMethod : offrampPaymentMethod}
                    onPaymentMethodChanged={method => {
                        if (rampDirection === 'ONRAMP') {
                            setOnrampPaymentMethod(method)
                        } else {
                            setOfframpPaymentMethod(method)
                        }
                    }}
                    sellAsset={rampDirection === 'OFFRAMP' ? sellToken?.symbol : undefined}
                    sellCurrency={rampDirection === 'ONRAMP' ? sellCurrency?.symbol : undefined}
                    // Allow payment methods to be fetched regardless of sell value
                    disabled={false}
                />

                <ExchangeButton
                    onExchange={onExchange}
                    isSessionTokenReady={!!sessionToken}
                    isQuoteFetching={
                        isQuoteFetching || isOnrampQuoteFetching || isOfframpQuoteFetching || isExchangeInProgress
                    }
                    isQuoteReady={
                        isQuoteReady && (rampDirection === 'ONRAMP' ? !!onrampPaymentMethod : !!offrampPaymentMethod)
                    }
                    requiredChainType={requiredChainType}
                    isSellValueZero={isSellValueZeroOrEmpty}
                />
            </div>
        </div>
    )
}

export default FiatExchange
